import { useState } from 'react';

import { Text } from '@gympass/yoga';
import { Tooltip } from '@mui/material';
import { useIntl } from 'react-intl';
import { getFullNameInitials } from 'utils/helpers';

import { useTimeFormat } from 'hooks';
import { useCareMatrix } from 'hooks/useCareMatrix';
import { SCREEN_MIN_WIDTH } from 'hooks/useComplaintDevice/useComplaintDevice';

import Counter from 'components/Counter';

import { ChatListItemProps } from './types';

import * as S from './styles';

const ChatListItem: React.FC<ChatListItemProps> = ({
  fullName,
  isSelected,
  avatarColor,
  unreadMessages,
  endUserCognitoId,
  closeNewMessageModal,
  lastMessageDeliveredAt,
  hasUnreadedMessage = false,
  daysUntilExpire,
  onSelect,
  hasScheduledMessage,
  isCritical,
  ...props
}) => {
  const intl = useIntl();
  const { getRelativeTime } = useTimeFormat();

  const [readed, setReaded] = useState(false);
  const isMobile = window.innerWidth < SCREEN_MIN_WIDTH;

  const { openModule } = useCareMatrix();

  const handleItemSelected = () => {
    setReaded(true);

    if (closeNewMessageModal) {
      closeNewMessageModal();
    }

    if (!isMobile) {
      openModule(endUserCognitoId);
    }

    onSelect?.(endUserCognitoId);
  };

  return (
    <S.ChatListItem
      isSelected={isSelected}
      onClick={handleItemSelected}
      id={`chat-list-item-${endUserCognitoId}`}
      data-testid={`chatListItem-${endUserCognitoId}`}
      {...props}
    >
      <S.ChatListItemAvatar $bgColor={avatarColor}>
        <Text color="white">{getFullNameInitials(fullName)}</Text>
      </S.ChatListItemAvatar>
      <S.ChatListItemName
        as={(hasUnreadedMessage && !readed) || isSelected ? Text.Medium : Text}
      >
        {fullName}
      </S.ChatListItemName>
      <S.ConversationDetails>
        {lastMessageDeliveredAt && (
          <S.ChatListItemLatest data-testid="lastMessageDelivered">
            <Text.Tiny color="medium" as="span">
              {getRelativeTime(lastMessageDeliveredAt, true)}
            </Text.Tiny>
          </S.ChatListItemLatest>
        )}

        <S.IconsWrapper>
          {hasScheduledMessage && (
            <Tooltip
              title={intl.formatMessage({
                id: 'CPTCCHAT.LABELTOOLTIP.SCHEDULEDMESSAGE',
              })}
            >
              <S.ScheduledMessageIcon />
            </Tooltip>
          )}

          {!!daysUntilExpire && (
            <Tooltip
              title={intl.formatMessage(
                {
                  id: 'ELIGIBILITYSHUTDOWN.TC.LABEL.UNKLINKDAYS',
                },
                {
                  Nº: daysUntilExpire,
                },
              )}
            >
              <S.UnlinkIcon />
            </Tooltip>
          )}

          {isCritical && (
            <Tooltip
              title={intl.formatMessage({
                id: 'CPTCCHAT.LABELTOOLTIP.CRITICAL',
              })}
            >
              <S.CriticalityIcon />
            </Tooltip>
          )}

          {hasUnreadedMessage && !readed && <Counter number={unreadMessages} />}
        </S.IconsWrapper>
      </S.ConversationDetails>
    </S.ChatListItem>
  );
};

export default ChatListItem;
